import { useMemo, useState } from "react";

import {
    Column,
    Icon,
    MEDIA,
    PriceLineChartWithAxis,
    Row,
    Text,
    ToggleSpacedButtons,
    useAppPalette,
    formatChartPercent,
    ChartSolidDotLegend,
    formatChartAmount
} from "@bridgesplit/ui";
import {
    getSamplingInterval,
    RatesComparisonPeriodSelect,
    useRatesComparisonPeriodSelect
} from "app/components/common";
import { bpsToUiDecimals, formatDate, formatPercent, getUnixTs, lamportsToUiAmount, TIME } from "@bridgesplit/utils";
import { BsMetaUtil, useLatestCollateralYieldHistoryByMint, useLendingVaultHistoryQuery } from "@bridgesplit/abf-react";
import { useMediaQuery } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import { useVaultContext } from "../../VaultContext";

enum Open {
    Apy = "APY",
    Deployed = "Deployed",
    Volume = "Volume",
    Utilization = "Utilization",
    Tvl = "TVL"
}

type Data = {
    date: number;
    apy: number | undefined;
    totalApy: number | undefined;
    externalRewardsApy: number | undefined;
    collateralYieldApy: number | undefined;
    deployed: number;
    volume: number;
    utilization: number;
    netAssetValue: number;
    activeLoanCount: number;
};

type ChartPoint = Data & {
    baseApy?: number;
    rewardsApy?: number;
    yieldApy?: number;
    totalApy?: number;
    normalizedActiveLoanCount?: number;
};

const DATA: Data[] = [
    {
        date: getUnixTs(),
        apy: 0.15,
        totalApy: 0.18,
        externalRewardsApy: 0.0,
        collateralYieldApy: 0.0,
        deployed: 3399,
        volume: 500,
        utilization: 0.7,
        netAssetValue: 10000,
        activeLoanCount: 25
    },
    {
        date: getUnixTs() + TIME.DAY,
        apy: 0.13,
        totalApy: 0.16,
        externalRewardsApy: 0.0,
        collateralYieldApy: 0.0,
        deployed: 10000,
        volume: 10000,
        utilization: 0.8,
        netAssetValue: 10000,
        activeLoanCount: 45
    }
];

const OPEN_TO_FORMAT: Record<Open, (value: number) => string> = {
    [Open.Apy]: formatChartPercent,
    [Open.Deployed]: formatChartAmount,
    [Open.Volume]: formatChartAmount,
    [Open.Utilization]: formatChartPercent,
    [Open.Tvl]: formatChartAmount
};

const OPEN_TO_KEY: Record<Open, keyof Data> = {
    [Open.Apy]: "apy",
    [Open.Deployed]: "deployed",
    [Open.Volume]: "volume",
    [Open.Utilization]: "utilization",
    [Open.Tvl]: "netAssetValue"
};

type ChartKey = keyof Data;
type ChartKeyConfig = { key: ChartKey; color?: string; dashed?: boolean };

const getChartKeys = (
    open: Open,
    hasAnyRewards: boolean,
    hasAnyYield: boolean,
    primary: string,
    secondary: string,
    info: string,
    prime: string
): ChartKeyConfig[] => {
    if (open === Open.Apy) {
        const keys: ChartKeyConfig[] = [
            { key: "baseApy" as ChartKey, color: primary } // always show base
        ];

        if (hasAnyRewards) {
            keys.push({ key: "rewardsApy" as ChartKey, color: prime });
        }

        if (hasAnyYield) {
            keys.push({ key: "yieldApy" as ChartKey, color: info });
        }

        if (hasAnyRewards || hasAnyYield) {
            keys.push({ key: "totalApy" as ChartKey, color: secondary });
        }

        return keys;
    }

    if (open === Open.Deployed) {
        return [
            { key: "deployed", color: primary },
            { key: "normalizedActiveLoanCount" as ChartKey, color: secondary, dashed: true }
        ];
    }

    return [{ key: OPEN_TO_KEY[open] }];
};

function ApyTooltipContent({ data }: { data: Data }) {
    const hasCollateralYield = data.collateralYieldApy && data.collateralYieldApy > 0;
    const hasExternalRewards = data.externalRewardsApy && data.externalRewardsApy > 0;

    const { primary, secondary, info, prime } = useAppPalette();
    const items = [
        <Row key="base" spacing={1} alignItems="center">
            <ChartSolidDotLegend color={primary} />
            <Text>Base APY: {formatPercent(data.apy ?? 0)}</Text>
        </Row>
    ];

    if (hasCollateralYield) {
        items.push(
            <Row key="collateral" spacing={1} alignItems="center">
                <ChartSolidDotLegend color={info} />
                <Text>Yield APY: {formatPercent(data.collateralYieldApy ?? 0)}</Text>
            </Row>
        );
    }

    if (hasExternalRewards) {
        items.push(
            <Row key="rewards" spacing={1} alignItems="center">
                <ChartSolidDotLegend color={prime} />
                <Text>Rewards APY: {formatPercent(data.externalRewardsApy ?? 0)}</Text>
            </Row>
        );
    }

    if (hasCollateralYield || hasExternalRewards) {
        items.push(
            <Row key="total" spacing={1} alignItems="center">
                <ChartSolidDotLegend color={secondary} />
                <Text>Total APY: {formatPercent(data.totalApy ?? 0)}</Text>
            </Row>
        );
    }

    // If only base APY, just show "APY: total" with polkadot legend
    if (items.length === 1) {
        return (
            <Row spacing={1} alignItems="center">
                <ChartSolidDotLegend color={primary} />
                <Text>APY: {formatPercent(data.totalApy ?? 0)}</Text>
            </Row>
        );
    }

    return <Column spacing={0.5}>{items}</Column>;
}

export default function VaultCharts() {
    const { vaultAddress, vaultExpanded } = useVaultContext();
    const periodController = useRatesComparisonPeriodSelect("VaultChartsPeriod");
    const { secondary, primary, info, prime } = useAppPalette();

    const { data: history } = useLendingVaultHistoryQuery(
        vaultAddress
            ? {
                  vaultAddress,
                  timeLookback: periodController.period,
                  sampleIntervalSecs: getSamplingInterval(periodController.period)
              }
            : skipToken,
        { skip: !vaultAddress }
    );

    const { data: collateralYields } = useLatestCollateralYieldHistoryByMint(
        vaultExpanded?.vault.principalMint ? [vaultExpanded?.vault.principalMint] : []
    );

    const principalMint = vaultExpanded?.vault.principalMint;
    const rawCollateralYieldApy = collateralYields && principalMint ? collateralYields[principalMint] : undefined;

    const [open, setOpen] = useState<Open>(Open.Apy);

    const data = useMemo(() => {
        const decimals = vaultExpanded?.principalMetadata.decimals;

        if (!history?.length) {
            return DATA;
        }
        return history?.map((h): Data => {
            const baseApy = bpsToUiDecimals(h.wAvgApy);
            const externalRewardsApy = h.externalRewardsApy ? bpsToUiDecimals(h.externalRewardsApy) : 0;

            //Does not need to be converted to ui decimals
            const collateralYield = rawCollateralYieldApy ? rawCollateralYieldApy : 0;
            const totalApy = baseApy + externalRewardsApy + collateralYield;
            return {
                date: h.timestamp,
                apy: baseApy,
                totalApy: totalApy,
                collateralYieldApy: collateralYield,
                externalRewardsApy: externalRewardsApy,
                deployed: lamportsToUiAmount(h.principalDeployed, decimals),
                volume: lamportsToUiAmount(h.cumulativeLoanVolume, decimals),
                utilization: h.utilizationRate ?? 0,
                netAssetValue: lamportsToUiAmount(h.netAssetValue, decimals),
                activeLoanCount: h.activeLoanCount ?? 0
            };
        });
    }, [history, vaultExpanded, rawCollateralYieldApy]);

    // Transform data into separate series for points with and without rewards
    const transformedData = useMemo(() => {
        if (open === Open.Apy) {
            return data.map((point) => {
                const hasRewards = point.externalRewardsApy !== 0;
                const hasYield = point.collateralYieldApy !== 0;

                const chartPoint: ChartPoint = {
                    ...point,
                    baseApy: point.apy
                };

                if (hasRewards) {
                    chartPoint.rewardsApy = point.externalRewardsApy;
                }

                if (hasYield) {
                    chartPoint.yieldApy = point.collateralYieldApy;
                }

                return chartPoint;
            });
        }

        if (open === Open.Deployed) {
            // Calculate normalization factor for active loan count to fit on deployed scale
            const maxDeployed = data.reduce((max, d) => Math.max(max, d.deployed), 0);
            const maxActiveLoanCount = data.reduce((max, d) => Math.max(max, d.activeLoanCount), 0);
            const normalizationFactor = maxDeployed / Math.max(maxActiveLoanCount, 1);

            // Always show deployed line, only add normalizedActiveLoanCount if not zero
            return data.map((point) => {
                if (point.activeLoanCount !== 0) {
                    return {
                        ...point,
                        normalizedActiveLoanCount: point.activeLoanCount * normalizationFactor
                    };
                }
                return point;
            });
        }

        return data;
    }, [data, open]);

    const hasAnyRewards = useMemo(() => {
        if (open !== Open.Apy) return false;
        return data.some((point) => point.externalRewardsApy && point.externalRewardsApy > 0);
    }, [data, open]);

    const hasAnyYield = useMemo(() => {
        if (open !== Open.Apy) return false;
        return data.some((point) => point.collateralYieldApy && point.collateralYieldApy > 0);
    }, [data, open]);

    const isMobile = useMediaQuery(MEDIA.MD.below);

    return (
        <>
            <Row spaceBetween sx={{ overflowX: "auto", gap: 2 }}>
                <ToggleSpacedButtons
                    size="small"
                    value={open}
                    setValue={setOpen}
                    options={Object.values(Open).map((o) => ({ value: o }))}
                />
                <RatesComparisonPeriodSelect {...periodController} />
            </Row>

            <PriceLineChartWithAxis
                keys={getChartKeys(open, hasAnyRewards, hasAnyYield, primary, secondary, info, prime)}
                height={200}
                minTickGap={50}
                formatYAxis={OPEN_TO_FORMAT[open]}
                formatXAxis={(seconds) => formatDate(seconds, "date", { showTime: "never" })}
                aspectRatio={isMobile ? 2 : 3}
                data={transformedData}
                tooltip={(point) => <Tooltip data={point} open={open} />}
            />
        </>
    );
}

function Tooltip({ data, open }: { data: Data | undefined; open: Open }) {
    const { vaultExpanded } = useVaultContext();
    const { primary, secondary } = useAppPalette();

    const formattedValue = useMemo(() => {
        if (!data) return null;

        if (open === Open.Apy) {
            return <ApyTooltipContent data={data} />;
        }

        if (open === Open.Deployed) {
            return (
                <Column spacing={0.5}>
                    <Row spacing={1} alignItems="center">
                        <ChartSolidDotLegend color={primary} />
                        <Text>
                            Deployed: {BsMetaUtil.formatAmount(vaultExpanded?.principalMetadata, data.deployed)}
                        </Text>
                    </Row>
                    {data.activeLoanCount !== 0 && (
                        <Row spacing={1} alignItems="center">
                            <ChartSolidDotLegend color={secondary} />
                            <Text>Active Loans: {data.activeLoanCount.toLocaleString()}</Text>
                        </Row>
                    )}
                </Column>
            );
        }

        if (open === Open.Volume) {
            return <Text>{BsMetaUtil.formatAmount(vaultExpanded?.principalMetadata, data.volume)}</Text>;
        }

        if (open === Open.Utilization) {
            return <Text>{formatPercent(data.utilization)}</Text>;
        }

        if (open === Open.Tvl) {
            return <Text>{BsMetaUtil.formatAmount(vaultExpanded?.principalMetadata, data.netAssetValue)}</Text>;
        }
    }, [data, open, vaultExpanded, primary, secondary]);

    return (
        <Column spacing={0.5}>
            {formattedValue}
            <Text variant="body2" color="caption">
                <Icon type="date" /> {formatDate(data?.date)}
            </Text>
        </Column>
    );
}
